import {
    ChangeEvent,
    FC,
    KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CommonButton } from '../CommonButton';
import {
    CommonModal,
    CommonModalContainer,
    CUSTOM_STYLES,
} from '../CommonModal';
import { ModalTitle } from '../ModalTitle';
import type { OnAfterOpenCallback } from 'react-modal';

export type AliasModalProps = {
    isOpen: boolean;
    saving: boolean;
    errorKey: string;
    initialValue?: string;
    onConfirm: (alias: string) => void;
};

const MAX_ALIAS_LENGTH = 15;

const AliasModalContainer = styled(CommonModalContainer)`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 44rem;
    padding: 3rem 4.813rem 3.5rem;
`;

const AliasModalTitle = styled(ModalTitle)`
    font-size: 2.875rem;
    line-height: 2.875rem;
    text-align: center;
`;

const Description = styled.p`
    margin: 1.25rem 0 0;
    font-family: Signika-SemiBold;
    font-size: 1.875rem;
    line-height: 2.375rem;
    text-align: center;
    color: ${({ theme }) => theme.commonModal.color.content};
`;

const AliasInput = styled.input`
    width: 100%;
    margin-top: 1.75rem;
    padding: 1rem 1.25rem;
    border-radius: 1rem;
    outline: none;
    font-family: Signika-SemiBold;
    font-size: 2rem;
    color: ${({ theme }) => theme.plusMinusInput.color};
    border: solid 0.094rem ${({ theme }) => theme.plusMinusInput.border};
    background-color: ${({ theme }) => theme.plusMinusInput.background};
    box-shadow: inset 0 0.25rem 0 0
            ${({ theme }) => theme.plusMinusInput.boxShadow.first},
        0 0.25rem 0 0 ${({ theme }) => theme.plusMinusInput.boxShadow.second};

    &::placeholder {
        color: ${({ theme }) => theme.commonModal.color.content};
        opacity: 0.7;
    }
`;

const ErrorText = styled.div`
    min-height: 2.25rem;
    margin-top: 1rem;
    font-family: Signika-SemiBold;
    font-size: 1.5rem;
    line-height: 1.75rem;
    text-align: center;
    color: #c62828;
`;

const ButtonWrapper = styled.div`
    margin-top: 1.5rem;
`;

export const AliasModal: FC<AliasModalProps> = ({
    isOpen,
    saving,
    errorKey,
    initialValue = '',
    onConfirm,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);

    const trimmedValue = value.trim();
    const initialTrimmedValue = initialValue.trim();

    const displayError = useMemo(() => {
        if (value.length > MAX_ALIAS_LENGTH) {
            return 'error.alias.length';
        }

        return errorKey;
    }, [errorKey, value.length]);

    const isUnchanged = trimmedValue === initialTrimmedValue;

    const isDisabled =
        saving ||
        trimmedValue.length === 0 ||
        value.length > MAX_ALIAS_LENGTH ||
        isUnchanged;

    const handleConfirm = () => {
        if (isDisabled) {
            return;
        }

        onConfirm(trimmedValue);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleConfirm();
        }
    };

    const onAfterOpen: OnAfterOpenCallback = () => {
        setValue(initialValue);
        inputRef.current?.focus();
    };

    useEffect(() => {
        if (isOpen) {
            setValue(initialValue);
        }
    }, [isOpen, initialValue]);

    return (
        <CommonModal
            isOpen={isOpen}
            onClose={() => {}}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            onAfterOpen={onAfterOpen}
            hideCloseButton
            Container={AliasModalContainer}
            style={{
                overlay: {
                    ...CUSTOM_STYLES.overlay,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                content: {
                    ...CUSTOM_STYLES.content,
                    position: 'static',
                    top: 'auto',
                    right: 'auto',
                    bottom: 'auto',
                    left: 'auto',
                    marginBottom: 0,
                },
            }}
        >
            <AliasModalTitle title={t('aliasModal.title')} />
            <Description>{t('aliasModal.description')}</Description>
            <AliasInput
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={t('aliasModal.inputPlaceholder')}
            />
            <ErrorText>{displayError ? t(displayError) : null}</ErrorText>
            <ButtonWrapper>
                <CommonButton handleClick={handleConfirm} disabled={isDisabled}>
                    {t('aliasModal.confirm')}
                </CommonButton>
            </ButtonWrapper>
        </CommonModal>
    );
};
